import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight, Bot, Database, Zap, Shield, BarChart3, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const PipedriveAIAgent = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 AI Agent for Pipedrive",
      "applicationCategory": "BusinessApplication",
      "description": "AI agent for Pipedrive that automatically updates deals, enriches contacts, logs all activities, and generates pipeline intelligence. No manual data entry.",
      "url": "https://noq5.ai/pipedrive-ai-agent"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "CRM Agent", "item": "https://noq5.ai/crm-agent" },
        { "@type": "ListItem", "position": 3, "name": "Pipedrive AI Agent", "item": "https://noq5.ai/pipedrive-ai-agent" }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="Pipedrive AI Agent — Automate Pipedrive with AI | NoQ5"
        description="The #1 AI agent for Pipedrive. Auto-update deals, enrich contacts, log activities from all channels, and forecast pipeline. Setup in 5 minutes. No manual Pipedrive data entry."
        keywords="Pipedrive AI agent, AI agent for Pipedrive, Pipedrive automation AI, automate Pipedrive, Pipedrive CRM agent, Pipedrive AI integration, Pipedrive AI assistant, AI update Pipedrive, Pipedrive deals automation, best AI for Pipedrive"
        canonicalUrl="https://noq5.ai/pipedrive-ai-agent"
        jsonLd={jsonLd}
      />

      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="text-sm text-muted-foreground mb-6">
              <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/crm-agent" className="hover:text-primary">CRM Agent</Link> / <span className="text-foreground">Pipedrive AI Agent</span>
            </nav>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              <span className="text-primary">Pipedrive AI Agent</span>: Make Pipedrive Run Itself
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Pipedrive is built for simplicity — but data entry is still manual. NoQ5 adds an AI agent layer that automatically updates deals, enriches contacts, captures all activities, and generates pipeline intelligence. Connected in 5 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/trial"><Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">Connect Pipedrive <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
              <Link to="/crm-agent"><Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 border-border text-foreground hover:bg-accent">Full CRM Agent</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">What the AI Agent Does in Pipedrive</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Bot, title: "Auto-Update Deals", desc: "Deal stage, value, expected close date, notes, and custom fields — all updated from conversation context after every meeting and email." },
              { icon: Database, title: "Enrich Contacts & Organizations", desc: "Fill missing person and organization fields — job title, company size, industry, phone, LinkedIn — from 15+ data sources automatically." },
              { icon: Zap, title: "Activity Logging", desc: "Calls, emails, Zoom meetings, Slack messages, and LinkedIn touchpoints logged to the right Pipedrive person and deal." },
              { icon: BarChart3, title: "Pipeline Intelligence", desc: "Deal health scores, win probability, and pipeline forecasts generated from actual engagement data — not rep guesses." },
              { icon: Users, title: "Contact Mapping", desc: "Track all stakeholders per deal. Detect single-threaded risks and suggest additional contacts to engage." },
              { icon: Shield, title: "Data Quality Enforcement", desc: "Ensure field completeness, standardize values, and maintain clean Pipedrive data from day one." },
            ].map((item) => (
              <div key={item.title} className="bg-card border border-border rounded-xl p-6">
                <item.icon className="h-8 w-8 text-primary mb-3" />
                <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <h2 className="text-3xl font-bold text-foreground">Why Pipedrive Teams Need an AI Agent</h2>
            <p>Pipedrive's strength is simplicity — clean interface, visual pipeline, easy setup. But that simplicity doesn't solve the fundamental problem: someone still has to enter the data. Your reps still need to log calls, update deal values, and add notes after every interaction.</p>
            <p>NoQ5 preserves everything you love about Pipedrive while eliminating everything you hate: manual data entry, incomplete records, stale pipeline data, and time wasted on CRM admin instead of selling.</p>

            <h3 className="text-2xl font-bold text-foreground">Pipedrive + NoQ5: How It Works</h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li><strong>Connect via OAuth</strong> — 5-minute setup. NoQ5 reads your Pipedrive pipelines, stages, and custom fields.</li>
              <li><strong>Connect communication tools</strong> — Gmail, Zoom, Slack, LinkedIn, Calendar.</li>
              <li><strong>Agent starts working</strong> — Every interaction captured, every deal updated, every contact enriched.</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">Keep Pipedrive's Simplicity. Eliminate the Data Entry.</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">Connect in 5 minutes. Every deal updated. Every contact enriched. Zero manual work.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial"><Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">Connect Pipedrive Free <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/crm-agent"><Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">Full CRM Agent</Button></Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">Related</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent</Link>
            <Link to="/hubspot-ai-agent" className="text-primary hover:underline text-sm">HubSpot AI Agent</Link>
            <Link to="/ai-agent-salesforce" className="text-primary hover:underline text-sm">Salesforce AI Agent</Link>
            <Link to="/crm-enrichment-agent" className="text-primary hover:underline text-sm">CRM Enrichment Agent</Link>
            <Link to="/crm-data-entry-automation" className="text-primary hover:underline text-sm">CRM Data Entry Automation</Link>
            <Link to="/comparison" className="text-primary hover:underline text-sm">All Comparisons</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PipedriveAIAgent;
